import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
    Card,
    Select,
    Button,
    Table,
    Space,
    Typography,
    Tooltip,
    Checkbox,
    Tag,
} from 'antd';
import {
    ReloadOutlined,
    CalendarOutlined,
    FileExcelOutlined,
    EyeOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getRequest } from 'api';
import { getStoredWorkMonth } from 'utils/workMonthStorage';
import { ClassLevelSelect } from 'Components';
import './ClassPaymentStatistics.css';

const { Text } = Typography;
const { Option } = Select;

interface StudentPaymentDetail {
    incomeSchoolId: number;
    code: string;
    name: string;
    carryUnits?: number | null;
    carryAmount?: number | null;
    monthUnits?: number | null;
    monthAmount?: number | null;
    remainingUnits?: number | null;
    remainingAmount?: number | null;
    collectedUnits?: number | null;
    collectedAmount?: number | null;
    paymentDate?: string | null;
    isRegistered: boolean;
}

interface StudentPaymentRow {
    studentClassYearId: number;
    studentId: number;
    studentCode: string;
    studentName: string;
    receiptNo: string;
    lastPaymentDate: string;
    collectorType: number;
    totalPaid: number;
    remainingAmount: number;
    mustCollectAmount: number;
    carryAmount?: number | null;
    monthAmount: number;
    description: string;
    details: StudentPaymentDetail[];
}

interface PaymentStatisticsBoardData {
    classYearId: number;
    className: string;
    schoolYear: string;
    month: number;
    year: number;
    totalStudents: number;
    totalPaid: number;
    totalMustCollect: number;
    totalRemaining: number;
    totalCarry: number;
    totalMonthAmount: number;
    paymentRound?: number;
    maxPaymentRound?: number;
    students: StudentPaymentRow[];
}

interface ClassYearOption {
    classYearId: number;
    className: string;
    schoolYear: string;
    classLevel: string;
}

const renderColumnHeaderWithTooltip = (label: string, tooltip: string) => (
    <Tooltip title={tooltip} placement="top">
        <span style={{ cursor: 'help', borderBottom: '1px dashed rgba(135, 56, 0, 0.45)', paddingBottom: 1 }}>
            {label}
        </span>
    </Tooltip>
);

export const ClassPaymentStatistics: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const schoolIdStr = localStorage.getItem('schoolId');
    const schoolId = schoolIdStr ? parseInt(schoolIdStr, 10) : null;

    const [selectedYear, setSelectedYear] = useState<dayjs.Dayjs | null>(() => dayjs(`${getStoredWorkMonth().year}`, 'YYYY'));
    const [selectedMonth, setSelectedMonth] = useState<number>(() => getStoredWorkMonth().month);
    const [selectedClassYearId, setSelectedClassYearId] = useState<number | null>(null);
    const [selectedClassLevel, setSelectedClassLevel] = useState<string | undefined>(undefined);
    const [selectedPaymentRound, setSelectedPaymentRound] = useState<number>(0); // 0: Tất cả các lần, 1: Lần 1, 2: Lần 2...

    const [classList, setClassList] = useState<ClassYearOption[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [boardData, setBoardData] = useState<PaymentStatisticsBoardData | null>(null);

    // Selected student for Detail table (defaults to first student)
    const [selectedStudentScyId, setSelectedStudentScyId] = useState<number | null>(null);

    // Checkbox "Hiện tất cả nguồn thu"
    const [showAllIncomes, setShowAllIncomes] = useState<boolean>(true);

    // Lắng nghe thay đổi kỳ làm việc từ cấu hình
    useEffect(() => {
        const handleWorkMonthChange = (e: any) => {
            const { month, year } = e.detail || getStoredWorkMonth();
            setSelectedMonth(month);
            setSelectedYear(dayjs(`${year}`, 'YYYY'));
            setSelectedPaymentRound(0);
        };
        window.addEventListener('workMonthChanged', handleWorkMonthChange);
        return () => window.removeEventListener('workMonthChanged', handleWorkMonthChange);
    }, []);

    // Fetch classes
    const fetchClasses = useCallback(async (year: number) => {
        if (!schoolId) return;
        try {
            const res: any = await getRequest(
                `/cms/class/get-class-years-by-start-end-schoolId?start=${year}&schoolId=${schoolId}&page=1&perPage=200&sort=asc&sortBy=className`
            );
            if (res?.code === 200 && res.info?.list) {
                setClassList(res.info.list);
                const paramClassId = searchParams.get('classYearId');
                if (paramClassId) {
                    const id = parseInt(paramClassId, 10);
                    if (res.info.list.some((c: any) => c.classYearId === id)) {
                        setSelectedClassYearId(id);
                        return;
                    }
                }
                if (!selectedClassYearId && res.info.list.length > 0) {
                    setSelectedClassYearId(res.info.list[0].classYearId);
                }
            }
        } catch {
            toast.error('Lỗi khi tải danh sách lớp');
        }
    }, [schoolId, searchParams, selectedClassYearId]);

    useEffect(() => {
        if (selectedYear) {
            fetchClasses(selectedYear.year());
        }
    }, [selectedYear, fetchClasses]);

    const filteredClassList = useMemo(() => {
        if (!selectedClassLevel) return classList;
        return classList.filter((c) => String(c.classLevel).trim() === selectedClassLevel.trim());
    }, [classList, selectedClassLevel]);

    const handleClassLevelChange = (level?: string) => {
        setSelectedClassLevel(level);
        const nextList = level
            ? classList.filter((c) => String(c.classLevel).trim() === level.trim())
            : classList;
        if (nextList.length > 0) {
            if (!nextList.some((c) => c.classYearId === selectedClassYearId)) {
                setSelectedClassYearId(nextList[0].classYearId);
                setSelectedPaymentRound(0);
                setSearchParams({ classYearId: String(nextList[0].classYearId) });
            }
        } else {
            setSelectedClassYearId(null);
            setBoardData(null);
        }
    };

    // Fetch payment statistics data
    const fetchPaymentData = useCallback(async () => {
        if (!selectedClassYearId || !selectedYear) return;
        setLoading(true);
        try {
            const yearNum = selectedYear.year();
            const roundParam = selectedPaymentRound > 0 ? `&paymentRound=${selectedPaymentRound}` : '';
            const res: any = await getRequest(
                `/cms/accountant/class-income/payment-statistics?classYearId=${selectedClassYearId}&month=${selectedMonth}&year=${yearNum}${roundParam}`
            );
            if (res?.code === 200 && res.info) {
                const data: PaymentStatisticsBoardData = res.info;
                if (selectedPaymentRound > 0 && data.maxPaymentRound && selectedPaymentRound > data.maxPaymentRound) {
                    setSelectedPaymentRound(0);
                    setShowAllIncomes(true);
                    return;
                }
                setBoardData(data);

                if (data.students && data.students.length > 0) {
                    const currentSelected = data.students.find((s) => s.studentClassYearId === selectedStudentScyId);
                    if (selectedPaymentRound > 0) {
                        // Khi lọc theo Lần thu, ưu tiên chọn học sinh có phát sinh nộp tiền ở lần thu đó nếu học sinh đang chọn chưa nộp
                        if (!currentSelected || !currentSelected.totalPaid || currentSelected.totalPaid <= 0) {
                            const firstPaidStudent = data.students.find((s) => s.totalPaid && s.totalPaid > 0);
                            setSelectedStudentScyId(
                                firstPaidStudent ? firstPaidStudent.studentClassYearId : data.students[0].studentClassYearId
                            );
                        }
                    } else if (!currentSelected) {
                        setSelectedStudentScyId(data.students[0].studentClassYearId);
                    }
                } else {
                    setSelectedStudentScyId(null);
                }
            } else {
                toast.error(res?.msg || 'Không thể tải thống kê thu tiền');
            }
        } catch {
            toast.error('Lỗi kết nối khi tải thống kê thu tiền');
        } finally {
            setLoading(false);
        }
    }, [selectedClassYearId, selectedMonth, selectedYear, selectedPaymentRound, selectedStudentScyId]);

    useEffect(() => {
        fetchPaymentData();
    }, [fetchPaymentData]);

    // Danh sách các Lần thu (Lần 1, Lần 2, ...) dựa trên maxPaymentRound của lớp
    const roundOptions = useMemo(() => {
        const maxRound = Math.max(1, boardData?.maxPaymentRound || 1, selectedPaymentRound);
        return Array.from({ length: maxRound }, (_, idx) => idx + 1);
    }, [boardData?.maxPaymentRound, selectedPaymentRound]);

    // Selected student object
    const selectedStudent = useMemo(() => {
        if (!boardData || !selectedStudentScyId) return null;
        return boardData.students.find((s) => s.studentClassYearId === selectedStudentScyId) || null;
    }, [boardData, selectedStudentScyId]);

    // Filtered detail list for the selected student
    const detailTableData = useMemo(() => {
        if (!selectedStudent || !selectedStudent.details) return [];
        if (showAllIncomes) {
            return selectedStudent.details;
        }
        // Khi đang lọc theo Lần thu cụ thể (Lần 1, Lần 2...) và tắt "Hiện tất cả nguồn thu":
        // Chỉ hiển thị đúng những khoản thu được thanh toán trong lần thu đó
        if (selectedPaymentRound > 0) {
            return selectedStudent.details.filter(
                (d) => Boolean(d.collectedAmount && d.collectedAmount > 0)
            );
        }
        // Ở chế độ "Tất cả các lần" và tắt "Hiện tất cả nguồn thu":
        // Hiển thị các khoản học sinh có đăng ký hoặc có phát sinh tiền
        return selectedStudent.details.filter(
            (d) =>
                d.isRegistered ||
                (d.monthAmount && d.monthAmount > 0) ||
                (d.collectedAmount && d.collectedAmount > 0) ||
                (d.carryAmount && d.carryAmount !== 0)
        );
    }, [selectedStudent, showAllIncomes, selectedPaymentRound]);

    // Master Table Columns (Bảng 1: Danh sách học sinh)
    const masterColumns: ColumnsType<StudentPaymentRow> = useMemo(() => [
        {
            title: 'STT',
            key: 'index',
            width: 55,
            align: 'center',
            render: (_, __, index) => <span className="stt-cell">{index + 1}</span>,
        },
        {
            title: 'Mã HS',
            dataIndex: 'studentCode',
            key: 'studentCode',
            width: 120,
            align: 'center',
            render: (val) => <span className="student-code-cell">{val || '-'}</span>,
        },
        {
            title: 'Tên đối tượng',
            dataIndex: 'studentName',
            key: 'studentName',
            width: 180,
            render: (val) => <Text strong>{val || '-'}</Text>,
        },
        {
            title: renderColumnHeaderWithTooltip(
                'Gần nhất',
                'Ngày thanh toán gần nhất của học sinh trong tháng (hoặc ngày nộp của lần thu đang chọn).'
            ),
            dataIndex: 'lastPaymentDate',
            key: 'lastPaymentDate',
            width: 105,
            align: 'center',
            render: (val) => val || '-',
        },
        {
            title: renderColumnHeaderWithTooltip(
                'Tiền thu',
                'Tổng số tiền học sinh đã thanh toán (trong cả tháng hoặc trong lần thu đang chọn).'
            ),
            dataIndex: 'totalPaid',
            key: 'totalPaid',
            width: 120,
            align: 'right',
            render: (val) => (
                <span className="text-amount-paid">
                    {val ? val.toLocaleString('vi-VN') : '0'}
                </span>
            ),
        },
        {
            title: renderColumnHeaderWithTooltip(
                'Còn P Thu',
                'Số tiền học sinh còn nợ chưa đóng (= Phải Thu - Tổng tiền đã thu lũy kế).'
            ),
            dataIndex: 'remainingAmount',
            key: 'remainingAmount',
            width: 120,
            align: 'right',
            render: (val) => (
                <span className={val > 0 ? 'text-amount-remaining' : ''}>
                    {val ? val.toLocaleString('vi-VN') : '0'}
                </span>
            ),
        },
        {
            title: renderColumnHeaderWithTooltip(
                'Phải Thu',
                'Tổng số tiền thực tế phải thu trong tháng sau khi đã trừ tiền thừa hoặc cộng nợ tháng trước (= Thu tháng - Còn thừa).'
            ),
            dataIndex: 'mustCollectAmount',
            key: 'mustCollectAmount',
            width: 120,
            align: 'right',
            render: (val) => (
                <span style={{ fontWeight: 600 }}>
                    {val ? val.toLocaleString('vi-VN') : '0'}
                </span>
            ),
        },
        {
            title: renderColumnHeaderWithTooltip(
                'Còn thừa',
                'Tổng số tiền thừa (+) hoặc nợ (-) từ tháng trước kết chuyển sang tháng này.'
            ),
            dataIndex: 'carryAmount',
            key: 'carryAmount',
            width: 100,
            align: 'right',
            render: (val) => (
                <span className="text-amount-carry">
                    {val ? val.toLocaleString('vi-VN') : ''}
                </span>
            ),
        },
        {
            title: renderColumnHeaderWithTooltip(
                'Thu tháng',
                'Tổng số tiền phát sinh theo đăng ký của riêng tháng hiện tại (chưa tính thừa/nợ tháng trước).'
            ),
            dataIndex: 'monthAmount',
            key: 'monthAmount',
            width: 120,
            align: 'right',
            render: (val) => (val ? val.toLocaleString('vi-VN') : '0'),
        },
        {
            title: 'Diễn giải',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
            render: (val) => <span style={{ color: '#595959' }}>{val || '-'}</span>,
        },
    ], []);

    // Detail Table Columns (Bảng 2: Chi tiết nguồn thu của học sinh đang chọn)
    const detailColumns: ColumnsType<StudentPaymentDetail> = useMemo(() => [
        {
            title: 'Mã',
            dataIndex: 'code',
            key: 'code',
            width: 70,
            align: 'center',
            render: (val) => <span className="code-tag">{val || '-'}</span>,
        },
        {
            title: 'Nội dung',
            dataIndex: 'name',
            key: 'name',
            width: 220,
            render: (val) => <Text strong style={{ color: '#262626' }}>{val || '-'}</Text>,
        },
        {
            title: renderColumnHeaderWithTooltip(
                'SL Thừa',
                'Số lượng (suất/buổi/tháng) thừa (+) hoặc nợ (-) từ tháng trước kết chuyển sang.'
            ),
            dataIndex: 'carryUnits',
            key: 'carryUnits',
            width: 80,
            align: 'center',
            render: (val) => val ?? '',
        },
        {
            title: renderColumnHeaderWithTooltip(
                'Tiền thừa ĐKý',
                'Số tiền thừa (+) được cấn trừ hoặc tiền nợ (-) từ tháng trước kết chuyển sang.'
            ),
            dataIndex: 'carryAmount',
            key: 'carryAmount',
            width: 120,
            align: 'right',
            render: (val) => (val ? val.toLocaleString('vi-VN') : ''),
        },
        {
            title: renderColumnHeaderWithTooltip(
                'SL tháng',
                'Số lượng đăng ký sử dụng của riêng tháng hiện tại.'
            ),
            dataIndex: 'monthUnits',
            key: 'monthUnits',
            width: 80,
            align: 'center',
            render: (val) => val ?? '',
        },
        {
            title: renderColumnHeaderWithTooltip(
                'Tiền tháng',
                'Thành tiền phát sinh của riêng tháng hiện tại (= SL tháng × Đơn giá).'
            ),
            dataIndex: 'monthAmount',
            key: 'monthAmount',
            width: 120,
            align: 'right',
            render: (val) => (val ? val.toLocaleString('vi-VN') : ''),
        },
        {
            title: renderColumnHeaderWithTooltip(
                'SL còn Pthu',
                'Số lượng thực tế cần phải thu sau khi cấn trừ SL thừa/nợ tháng trước (và trừ các lần thu trước đó nếu đang lọc theo lần).'
            ),
            dataIndex: 'remainingUnits',
            key: 'remainingUnits',
            width: 90,
            align: 'center',
            render: (val) => (val !== null && val !== undefined ? val : ''),
        },
        {
            title: renderColumnHeaderWithTooltip(
                'Tiền còn Pthu',
                'Số tiền thực tế cần phải thu sau khi cấn trừ tiền thừa/nợ tháng trước (và trừ các lần thu trước đó nếu đang lọc theo lần).'
            ),
            dataIndex: 'remainingAmount',
            key: 'remainingAmount',
            width: 120,
            align: 'right',
            render: (val) => (
                <span className="detail-blue-cell">
                    {val ? val.toLocaleString('vi-VN') : ''}
                </span>
            ),
        },
        {
            title: renderColumnHeaderWithTooltip(
                'SL đã thu',
                'Số lượng tương ứng với số tiền học sinh đã thanh toán.'
            ),
            dataIndex: 'collectedUnits',
            key: 'collectedUnits',
            width: 85,
            align: 'center',
            render: (val) => (
                <span className="detail-blue-cell">
                    {val ?? ''}
                </span>
            ),
        },
        {
            title: renderColumnHeaderWithTooltip(
                'Tiền đã thu',
                'Số tiền học sinh đã thanh toán cho khoản thu này (trong cả tháng hoặc trong lần thu đang chọn).'
            ),
            dataIndex: 'collectedAmount',
            key: 'collectedAmount',
            width: 120,
            align: 'right',
            render: (val) => (
                <span className="detail-blue-cell">
                    {val ? val.toLocaleString('vi-VN') : ''}
                </span>
            ),
        },
        {
            title: renderColumnHeaderWithTooltip(
                'Ngày thanh toán',
                'Ngày học sinh thanh toán khoản thu này.'
            ),
            dataIndex: 'paymentDate',
            key: 'paymentDate',
            width: 115,
            align: 'center',
            render: (val) => (
                <span className="detail-blue-cell">
                    {val || ''}
                </span>
            ),
        },
    ], []);

    // Export CSV / Excel file
    const handleExportExcel = () => {
        if (!boardData || !boardData.students || boardData.students.length === 0) {
            toast.warning('Chưa có dữ liệu để xuất file!');
            return;
        }

        const headers = [
            'STT',
            'Mã HS',
            'Tên đối tượng',
            'Gần nhất',
            'Tiền thu',
            'Còn P Thu',
            'Phải Thu',
            'Còn thừa',
            'Thu tháng',
            'Diễn giải',
        ];

        const rows = boardData.students.map((s, index) => [
            index + 1,
            `"${s.studentCode || ''}"`,
            `"${s.studentName || ''}"`,
            `"${s.lastPaymentDate || ''}"`,
            s.totalPaid || 0,
            s.remainingAmount || 0,
            s.mustCollectAmount || 0,
            s.carryAmount || 0,
            s.monthAmount || 0,
            `"${s.description || ''}"`,
        ]);

        const roundSuffix = selectedPaymentRound > 0 ? `_Lan_${selectedPaymentRound}` : '';
        const csvContent = '\uFEFF' + [headers.join(','), ...rows.map((r) => r.join(','))].join('\r\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute(
            'download',
            `Theo_doi_thu_nop_${boardData.className || 'Lop'}_Thang_${selectedMonth}_${selectedYear?.year()}${roundSuffix}.csv`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.success('Đã xuất file báo cáo theo dõi thu nộp thành công!');
    };

    return (
        <div className="class-payment-statistics-page">
            {/* TOP TOOLBAR */}
            <Card bordered={false} className="header-card" bodyStyle={{ padding: '8px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                    {/* Left: Filters */}
                    <Space size={16} align="center" wrap>
                        <Space size={6} align="center">
                            <Text strong style={{ color: '#595959', fontSize: 13 }}>Khối:</Text>
                            <ClassLevelSelect
                                value={selectedClassLevel}
                                onChange={handleClassLevelChange}
                                placeholder="Tất cả khối"
                                style={{ width: 130 }}
                            />
                        </Space>

                        <Space size={6} align="center">
                            <Text strong style={{ color: '#595959', fontSize: 13 }}>Lớp:</Text>
                            <Select
                                value={selectedClassYearId || undefined}
                                onChange={(val) => {
                                    setSelectedClassYearId(val);
                                    setSelectedPaymentRound(0);
                                    setShowAllIncomes(true);
                                    setSearchParams({ classYearId: String(val) });
                                }}
                                placeholder="Chọn lớp"
                                style={{ width: 180 }}
                                showSearch
                                optionFilterProp="children"
                            >
                                {filteredClassList.map((c) => (
                                    <Option key={c.classYearId} value={c.classYearId}>
                                        {c.className}
                                    </Option>
                                ))}
                            </Select>
                        </Space>

                        <Space size={6} align="center">
                            <Text strong style={{ color: '#595959', fontSize: 13 }}>Lần thu:</Text>
                            <Select
                                value={selectedPaymentRound}
                                onChange={(val) => {
                                    setSelectedPaymentRound(val);
                                    if (val > 0) {
                                        // Khi chọn Lần 1, Lần 2... tự động tắt "Hiện tất cả nguồn thu" để chỉ hiện đúng các khoản nộp trong lần đó
                                        setShowAllIncomes(false);
                                    } else {
                                        setShowAllIncomes(true);
                                    }
                                }}
                                style={{ width: 145 }}
                            >
                                <Option value={0}>Tất cả các lần</Option>
                                {roundOptions.map((r) => (
                                    <Option key={r} value={r}>
                                        Lần {r}
                                    </Option>
                                ))}
                            </Select>
                        </Space>

                        <Tag
                            color="blue"
                            style={{
                                fontSize: 13,
                                padding: '3px 10px',
                                borderRadius: 4,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                fontWeight: 600,
                            }}
                        >
                            <CalendarOutlined /> Tháng {selectedMonth}/{selectedYear ? selectedYear.year() : ''}
                        </Tag>
                    </Space>

                    {/* Right: Actions */}
                    <Space size={10} align="center">
                        <Tooltip title="Tải lại dữ liệu">
                            <Button icon={<ReloadOutlined />} onClick={fetchPaymentData} loading={loading} />
                        </Tooltip>
                        <Button icon={<FileExcelOutlined />} onClick={handleExportExcel}>
                            Xuất Excel
                        </Button>
                    </Space>
                </div>
            </Card>

            {/* BẢNG TRÊN: MASTER TABLE (DANH SÁCH HỌC SINH) */}
            <Card bordered={false} className="master-table-card" bodyStyle={{ padding: 8 }}>
                <Table<StudentPaymentRow>
                    columns={masterColumns}
                    dataSource={boardData?.students || []}
                    rowKey="studentClassYearId"
                    loading={loading}
                    pagination={false}
                    bordered
                    size="small"
                    rowClassName={(record) => (record.studentClassYearId === selectedStudentScyId ? 'row-selected' : '')}
                    onRow={(record) => ({
                        onClick: () => {
                            setSelectedStudentScyId(record.studentClassYearId);
                        },
                        style: { cursor: 'pointer' },
                    })}
                    scroll={{ x: 1210, y: 'calc(45vh - 70px)' }}
                />
            </Card>

            {/* BẢNG DƯỚI: DETAIL TABLE (CHI TIẾT NGUỒN THU CỦA HỌC SINH ĐANG CHỌN) */}
            <Card bordered={false} className="detail-table-card" bodyStyle={{ padding: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                    <Space size={8}>
                        <EyeOutlined style={{ color: '#096dd9' }} />
                        <span style={{ fontWeight: 700, fontSize: 13 }}>
                            Chi tiết nguồn thu{selectedPaymentRound > 0 ? ` (Lần ${selectedPaymentRound})` : ''}:{' '}
                            <span style={{ color: '#096dd9' }}>
                                {selectedStudent ? `${selectedStudent.studentName} (${selectedStudent.studentCode || 'Chưa có mã'})` : '(Chọn một học sinh ở bảng trên)'}
                            </span>
                        </span>
                    </Space>
                </div>

                <Table<StudentPaymentDetail>
                    columns={detailColumns}
                    dataSource={detailTableData}
                    rowKey="incomeSchoolId"
                    loading={loading}
                    pagination={false}
                    bordered
                    size="small"
                    scroll={{ x: 1180, y: 'calc(35vh - 70px)' }}
                />

                <div style={{ marginTop: 8, paddingLeft: 4 }}>
                    <Checkbox
                        checked={showAllIncomes}
                        onChange={(e) => setShowAllIncomes(e.target.checked)}
                    >
                        <span style={{ fontSize: 12.5, fontWeight: 500, color: '#595959' }}>
                            Hiện tất cả nguồn thu
                        </span>
                    </Checkbox>
                </div>
            </Card>
        </div>
    );
};

export default ClassPaymentStatistics;
